import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { AlertCircle, CheckCircle2, FileText, Loader2, LockKeyhole, Plus, RefreshCw, Sparkles, Upload } from "lucide-react";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AdminPimListResponse, Cohort, PimListRecord, PimStatus, UploadHandshake, mapAdminList, putSignedPdf, researchData,
} from "./research-pipeline-contracts";

type UploadValues = { companyName: string; disclosureVersion: string; cohortIds: number[] };

const statusLabel: Record<PimStatus, string> = {
  uploaded: "Uploaded", draft: "Draft", in_review: "In review",
  changes_requested: "Changes requested", approved: "Approved", published: "Published",
  withdrawn: "Withdrawn", archived: "Archived",
};

export default function AdminResearchPipeline() {
  const [, navigate] = useLocation();
  const [items, setItems] = useState<PimListRecord[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState("");
  const form = useForm<UploadValues>({ defaultValues: { companyName: "", disclosureVersion: "", cohortIds: [] } });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const mapped = mapAdminList(await researchData<AdminPimListResponse>("/pims"));
      setItems(mapped.items); setCohorts(mapped.cohorts);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not load Research Pipeline."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => ({
    processing: items.filter(({ pim, version }) => pim.status === "uploaded" || version?.processingStatus === "processing").length,
    review: items.filter(({ pim }) => pim.status === "in_review" || pim.status === "changes_requested").length,
    live: items.filter(({ pim }) => pim.status === "published").length,
  }), [items]);

  const runnableItems = useMemo(() => items.filter(({ pim }) => !["published", "withdrawn", "archived", "in_review"].includes(pim.status)), [items]);
  const runSelected = async () => {
    if (!selectedIds.length) { setError("Select at least one company."); return; }
    setRunning(true); setError(""); setNotice("");
    const failures: string[] = [];
    for (const pimId of selectedIds) {
      const company = items.find(({ pim }) => pim.id === pimId)?.pim.companyName || `Company ${pimId}`;
      try {
        await researchData(`/pims/${pimId}/ai-draft`, { method: "POST" });
      } catch {
        failures.push(company);
      }
    }
    await load();
    setRunning(false);
    if (failures.length) {
      setError(`Research failed for: ${failures.join(", ")}. Nothing was removed; retry those companies.`);
      return;
    }
    setSelectedIds([]);
    setNotice("Research complete. Kavya can now answer investors using the new company briefs.");
  };

  const upload = form.handleSubmit(async (values) => {
    if (!file) { setError("Select a private PDF to upload."); return; }
    if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) { setError("Only PDF files are accepted."); return; }
    if (!values.cohortIds.length) { setError("Select at least one cohort."); return; }
    setUploading(true); setError("");
    try {
      const handshake = await researchData<UploadHandshake>("/uploads", {
        method: "POST",
        body: JSON.stringify({ filename: file.name, size: file.size, contentType: "application/pdf" }),
      });
      await putSignedPdf(handshake, file);
      const created = await researchData<{ pim: { id: number } }>("/pims", {
        method: "POST",
        body: JSON.stringify({
          companyName: values.companyName,
          objectPath: handshake.objectPath,
          filename: file.name,
          cohortIds: values.cohortIds,
          disclosureVersion: values.disclosureVersion,
        }),
      });
      await load();
      navigate(`/admin/research-pipeline/${created.pim.id}`);
    } catch (e) { setError(e instanceof Error ? e.message : "Upload failed."); }
    finally { setUploading(false); }
  });

  return (
    <div className="space-y-7">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF5C00]">Private editorial research</p>
          <h1 className="mt-1 text-3xl font-serif tracking-tight">Research Pipeline</h1>
          <Link href="/admin/email?tab=compose" data-testid="link-email-cohort" className="mt-1 inline-block text-xs font-semibold text-[#FF5C00] hover:underline">Prepare a cohort email for approval in Email</Link>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">Select companies and run once. Meera prepares the research and Kavya receives the same brief for investor questions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void load()} disabled={loading} data-testid="button-refresh-research">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
          <Button onClick={() => setShowUpload((v) => !v)} data-testid="button-toggle-upload">
            <Plus className="mr-2 h-4 w-4" />Upload PIM
          </Button>
        </div>
      </header>

      {error && <div role="alert" data-testid="status-research-error" className="flex gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      {notice && <div role="status" className="flex gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4 shrink-0" />{notice}</div>}

      {showUpload && (
        <section className="rounded-lg border border-border bg-card p-5" aria-labelledby="upload-heading">
          <div className="mb-5 flex items-start gap-3"><LockKeyhole className="mt-0.5 h-5 w-5 text-[#FF5C00]" /><div><h2 id="upload-heading" className="font-semibold">Upload a private source PDF</h2><p className="text-xs text-muted-foreground">The source remains private. Creating this record does not publish it or contact approvers.</p></div></div>
          <Form {...form}>
            <form onSubmit={upload} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField control={form.control} name="companyName" rules={{ required: "Company name is required." }} render={({ field }) => <FormItem><FormLabel>Company name</FormLabel><FormControl><Input {...field} data-testid="input-company-name" /></FormControl><FormMessage /></FormItem>} />
                <div className="space-y-2">
                  <Label htmlFor="research-source-pdf">Source PDF</Label>
                  <Input id="research-source-pdf" type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="input-source-pdf" />
                </div>
                <FormField control={form.control} name="disclosureVersion" rules={{ required: "Disclosure version is required." }} render={({ field }) => <FormItem><FormLabel>Disclosure version</FormLabel><FormControl><Input {...field} data-testid="input-disclosure-version" /></FormControl><FormMessage /></FormItem>} />
              </div>
              <FormField control={form.control} name="cohortIds" rules={{ validate: (v) => v.length > 0 || "Select at least one cohort." }} render={({ field }) => (
                <FormItem><FormLabel>Initial cohort grants</FormLabel><div className="grid gap-2 sm:grid-cols-2">
                  {cohorts.map((cohort) => <label key={cohort.id} className="flex items-start gap-3 rounded-md border p-3 text-sm" data-testid={`label-upload-cohort-${cohort.id}`}>
                    <Checkbox checked={field.value.includes(cohort.id)} onCheckedChange={(checked) => field.onChange(checked ? [...field.value, cohort.id] : field.value.filter((id) => id !== cohort.id))} data-testid={`checkbox-upload-cohort-${cohort.id}`} />
                    <span><span className="font-medium">{cohort.name}</span><span className="block text-xs text-muted-foreground">Explicit grant candidate</span></span>
                  </label>)}
                </div><FormMessage /></FormItem>
              )} />
              <Button type="submit" disabled={uploading} data-testid="button-upload-private-pdf">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Create private draft</Button>
            </form>
          </Form>
        </section>
      )}

      <div className="grid grid-cols-3 divide-x rounded-lg border border-border">
        {[["Processing", counts.processing], ["Awaiting review", counts.review], ["Published", counts.live]].map(([label, value]) => <div key={label} className="p-4"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-xl font-semibold" data-testid={`text-count-${String(label).toLowerCase().replace(" ", "-")}`}>{value}</p></div>)}
      </div>

      {!!runnableItems.length && <section className="rounded-lg border border-[#FF5C00]/30 bg-[#FF5C00]/5 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div><h2 className="font-semibold">Run research</h2><p className="text-xs text-muted-foreground">Choose one or more companies below. One run updates the research draft and Kavya’s investor brief.</p></div>
          <Button onClick={() => void runSelected()} disabled={running || !selectedIds.length} data-testid="button-run-selected-research">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {running ? "Running…" : `Run selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </Button>
        </div>
      </section>}

      {loading && !items.length ? <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" data-testid="status-research-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading private research…</div>
      : !items.length ? <div className="rounded-lg border border-dashed p-12 text-center" data-testid="status-research-empty"><FileText className="mx-auto mb-3 h-6 w-6 text-muted-foreground" /><h2 className="font-medium">No research records yet</h2><p className="mt-1 text-sm text-muted-foreground">Upload a finished PDF to begin a private, reviewable draft.</p></div>
      : <div className="overflow-hidden rounded-lg border border-border"><div className="hidden grid-cols-[auto_2fr_1fr_1fr_1fr] gap-4 border-b bg-muted/40 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground md:grid"><span /><span>Company</span><span>Status</span><span>Access</span><span>Approval audit</span></div>
          {items.map(({ pim, version, approvals }) => { const runnable = !["published", "withdrawn", "archived", "in_review"].includes(pim.status); return <div key={pim.id} className="grid gap-3 border-b px-4 py-4 last:border-0 md:grid-cols-[auto_2fr_1fr_1fr_1fr] md:items-center">
            <Checkbox checked={selectedIds.includes(pim.id)} disabled={!runnable || running} onCheckedChange={(checked) => setSelectedIds((ids) => checked ? [...ids, pim.id] : ids.filter((id) => id !== pim.id))} aria-label={`Select ${pim.companyName}`} data-testid={`checkbox-research-${pim.id}`} />
            <Link href={`/admin/research-pipeline/${pim.id}`} className="transition-colors hover:text-[#FF5C00]" data-testid={`link-research-${pim.id}`}><p className="font-serif text-lg">{pim.companyName}</p><p className="mt-1 text-xs text-muted-foreground">{[version?.sector, version?.geography].filter(Boolean).join(" · ") || "Metadata pending"} · v{version?.version || 1}</p>{version?.processingError && <p className="mt-1 text-xs text-red-700">{version.processingError}</p>}</Link>
            <span className="w-fit rounded-full border px-2 py-1 text-xs" data-testid={`status-research-${pim.id}`}>{statusLabel[pim.status]}</span>
            <span className="text-xs text-muted-foreground">{version?.cohortIds?.length ? version.cohortIds.map((id) => cohorts.find((c) => c.id === id)?.name || `Cohort ${id}`).join(", ") : "No cohorts"}</span>
            <div className="text-xs"><span className={approvals.pc?.decision === "approved" ? "text-emerald-700" : "text-muted-foreground"}>PC: {approvals.pc?.decision.replace("_", " ") || "pending"}</span><br /><span className={approvals.bharat?.decision === "approved" ? "text-emerald-700" : "text-muted-foreground"}>Bharat: {approvals.bharat?.decision.replace("_", " ") || "pending"}</span><span className="mt-1 block text-[10px] text-muted-foreground">First approval completes the gate; the other is optional.</span></div>
          </div>; })}
        </div>}
    </div>
  );
}